import React, { useState, useEffect } from 'react';
import { apiClient } from '@services/api';
import Icon from '@material-ui/core/Icon';
import useIsNarrow from '@utils/useIsNarrow';

// Tela "Segurança dos pagamentos" (2026-10-20): resumo de 7, 15 ou 30 dias dos pedidos de
// liberação de cartão (liberados / recusados), do antifraude e das contestações.
// Só leitura — quem decide os pedidos pendentes continua sendo o ícone ! do cabeçalho.
// Fonte: GET /payment-security/summary?days= (PaymentSecurityController, só gestão).

const FRONT_LABEL = { site: 'Site', presale: 'Orçamento (presale)', app: 'App', signup: 'Cadastro', outro: 'Outro' };

const REASON_LABEL = {
  turnstile_falhou: 'CAPTCHA falhou',
  turnstile_causa: 'Causa do CAPTCHA',
  turnstile: 'CAPTCHA',
  cartao_muitas_tentativas: 'Cartão com muitas tentativas',
  ip_muitas_tentativas: 'IP com muitas tentativas',
  email_muitas_tentativas: 'E-mail com muitas tentativas',
  documento_muitas_tentativas: 'Documento com muitas tentativas',
  cadastros_por_ip: 'Muitos cadastros no mesmo IP',
  email_descartavel: 'E-mail descartável',
  email_padrao_suspeito: 'E-mail com padrão suspeito',
  telefone_pais_divergente: 'Telefone de outro país x celular BR',
  compra_madrugada_pos_cadastro: 'Compra de madrugada logo após cadastro',
  dados_divergem_cadastro: 'Dados da compra diferem do cadastro',
  ligado_conta_bloqueada: 'Mesmo CPF/WhatsApp de conta bloqueada',
  cep_invalido: 'CEP inválido',
  ip_mudou_apos_login: 'IP mudou após o login',
};
const reasonText = (r) => REASON_LABEL[r] || String(r).replace(/_/g, ' ');

const brl = (v) => 'R$ ' + Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDT = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? '—' : d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};
const fmtDate = (s) => {
  if (!s) return '—';
  const [y, m, d] = String(s).slice(0, 10).split('-');
  return d && m ? `${d}/${m}/${y}` : '—';
};
const fmtMin = (m) => {
  if (m === null || m === undefined) return '—';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return h < 24 ? `${h} h ${m % 60} min` : `${Math.floor(h / 24)} d ${h % 24} h`;
};

const STATUS = {
  pending:  { label: 'Aguardando', bg: 'rgba(168,85,247,0.15)', color: '#d8b4fe' },
  approved: { label: 'Liberado',   bg: 'rgba(16,185,129,0.15)', color: '#34d399' },
  rejected: { label: 'Recusado',   bg: 'rgba(239,68,68,0.15)',  color: '#f87171' },
};

const card = { background: '#0f2744', border: '1px solid #1e3a5f', borderRadius: 10, padding: '14px 16px', color: '#e6eefb' };
const label = { fontSize: 11, color: '#8ba5c4', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.4 };
const big = (color) => ({ fontSize: 26, fontWeight: 700, color: color || '#e6eefb', lineHeight: 1.1 });
const sectionTitle = { fontSize: 15, fontWeight: 700, color: '#62DEC2', margin: '22px 0 10px' };

const Stat = ({ title, value, color, sub }) => (
  <div style={{ ...card, flex: '1 1 150px', minWidth: 140 }}>
    <div style={label}>{title}</div>
    <div style={big(color)}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: '#8ba5c4', marginTop: 4 }}>{sub}</div>}
  </div>
);

const Index = () => {
  const narrow = useIsNarrow();
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    apiClient.httpInstance.get('/payment-security/summary', { params: { days } })
      .then((res) => { if (alive) setData(res.data); })
      .catch((e) => { if (alive) setError(e?.response?.status === 403 ? 'Sem permissão para ver esta tela.' : 'Não foi possível carregar o resumo.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  const rel = data?.releases;
  const af = data?.antifraud;
  const cb = data?.chargebacks;
  const maxDaily = Math.max(1, ...((af?.daily || []).map((d) => d.allowed + d.blocked)));

  return (
    <div className="px-7" style={{ paddingBottom: 40 }}>
      <div className="flex items-center mt-2 text-2xl text-blue mb-3">
        <Icon className="mr-2">verified_user</Icon>
        <span>Segurança dos pagamentos</span>
      </div>

      <div className="flex gap-2 mb-4" style={{ flexWrap: 'wrap' }}>
        {[7, 15, 30].map((d) => (
          <button key={d} type="button" onClick={() => setDays(d)}
            style={{
              padding: '7px 18px', borderRadius: 20, fontWeight: 600, fontSize: 13, cursor: 'pointer',
              flex: narrow ? '1 1 auto' : undefined,
              border: days === d ? '2px solid #47B098' : '1px solid #1e3a5f',
              background: days === d ? 'rgba(71,176,152,0.15)' : 'transparent',
              color: days === d ? '#62DEC2' : '#8a9ab5',
            }}>Últimos {d} dias</button>
        ))}
      </div>

      {loading && <div style={{ color: '#8ba5c4' }}>Carregando…</div>}
      {error && <div style={{ color: '#f87171' }}>{error}</div>}

      {!loading && !error && data && (
        <>
          {/* ── Orçamentos com cartão travado (agora) ── */}
          <div style={{ ...sectionTitle, marginTop: 4 }}>🔒 Orçamentos com cartão travado (agora)</div>
          {data.holds ? (
            <>
              <div className="flex gap-3" style={{ flexWrap: 'wrap' }}>
                <Stat title="Travados" value={data.holds.total} color={data.holds.total > 0 ? '#d8b4fe' : undefined} sub="todas as datas" />
                <Stat title="Pediram liberação" value={data.holds.waiting} color={data.holds.waiting > 0 ? '#d8b4fe' : undefined} sub={data.holds.waiting > 0 ? 'decidir no ícone !' : undefined} />
              </div>
              {data.holds.items.length === 0 ? (
                <div style={{ color: '#8ba5c4', marginTop: 12 }}>Nenhum orçamento com cartão travado.</div>
              ) : (
                <div style={{ ...card, marginTop: 12, padding: 0, overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ color: '#8ba5c4', textAlign: 'left' }}>
                        {['Orçamento', 'Cliente', 'Travado por', 'Quando', 'Motivo', 'Consultor', 'Liberação'].map((h) => (
                          <th key={h} style={{ padding: '10px 12px', borderBottom: '1px solid #1e3a5f', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.holds.items.map((h) => {
                        const st = h.release_status ? STATUS[h.release_status] : null;
                        return (
                          <tr key={h.id} style={{ borderBottom: '1px solid #16304f' }}>
                            <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{h.ref}</td>
                            <td style={{ padding: '8px 12px' }}>
                              <div style={{ fontWeight: 600 }}>{h.customer_name || '—'}</div>
                              <div style={{ color: '#8ba5c4', fontSize: 12 }}>{h.email || ''}</div>
                            </td>
                            <td style={{ padding: '8px 12px' }}>{h.locked_by_name || '—'}</td>
                            <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{fmtDT(h.locked_at)}</td>
                            <td style={{ padding: '8px 12px' }}>{h.reason || '—'}</td>
                            <td style={{ padding: '8px 12px' }}>{h.owner_name || '—'}</td>
                            <td style={{ padding: '8px 12px' }}>
                              {st ? <span style={{ background: st.bg, color: st.color, borderRadius: 12, padding: '3px 10px', fontWeight: 600, fontSize: 12 }}>● {st.label}</span>
                                : <span style={{ color: '#8ba5c4' }}>Sem pedido</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : <div style={{ color: '#8ba5c4' }}>Indisponível no momento.</div>}

          {/* ── Pedidos de liberação de cartão ── */}
          <div style={sectionTitle}>🔐 Pedidos de liberação de cartão (site)</div>
          {rel ? (
            <>
              <div className="flex gap-3" style={{ flexWrap: 'wrap' }}>
                <Stat title="Pedidos" value={rel.summary.total} sub={brl(rel.summary.amount_total)} />
                <Stat title="Liberados" value={rel.summary.approved} color="#34d399" sub={brl(rel.summary.amount_approved)} />
                <Stat title="Recusados" value={rel.summary.rejected} color="#f87171" sub={brl(rel.summary.amount_rejected)} />
                <Stat title="Aguardando" value={rel.summary.pending} color={rel.summary.pending > 0 ? '#d8b4fe' : undefined} sub={rel.summary.pending > 0 ? 'decidir no ícone !' : undefined} />
                <Stat title="Tempo médio de resposta" value={fmtMin(rel.summary.avg_response_minutes)} />
              </div>

              {rel.items.length === 0 ? (
                <div style={{ color: '#8ba5c4', marginTop: 12 }}>Nenhum pedido de liberação no período.</div>
              ) : (
                <div style={{ ...card, marginTop: 12, padding: 0, overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ color: '#8ba5c4', textAlign: 'left' }}>
                        {['Pedido', 'Cliente', 'Fatura', 'Valor', 'Passeio', 'Status', 'Decidido por', 'Decisão'].map((h) => (
                          <th key={h} style={{ padding: '10px 12px', borderBottom: '1px solid #1e3a5f', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rel.items.map((r) => {
                        const st = STATUS[r.status] || STATUS.pending;
                        return (
                          <tr key={r.id} style={{ borderBottom: '1px solid #16304f' }}>
                            <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{fmtDT(r.requested_at)}</td>
                            <td style={{ padding: '8px 12px' }}>
                              <div style={{ fontWeight: 600 }}>{r.customer_name || '—'}</div>
                              <div style={{ color: '#8ba5c4', fontSize: 12 }}>{r.email || ''}</div>
                            </td>
                            <td style={{ padding: '8px 12px' }}>{r.ref}</td>
                            <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{brl(r.amount)}</td>
                            <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{fmtDate(r.tour_date)}</td>
                            <td style={{ padding: '8px 12px' }}>
                              <span style={{ background: st.bg, color: st.color, borderRadius: 12, padding: '3px 10px', fontWeight: 600, fontSize: 12 }}>● {st.label}</span>
                            </td>
                            <td style={{ padding: '8px 12px' }}>{r.decided_by_name || '—'}</td>
                            <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{fmtDT(r.decided_at)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : <div style={{ color: '#8ba5c4' }}>Indisponível no momento.</div>}

          {/* ── Antifraude ── */}
          <div style={sectionTitle}>🛡️ Antifraude do cartão</div>
          {af ? (
            <>
              <div className="flex gap-3" style={{ flexWrap: 'wrap' }}>
                <Stat title="Tentativas verificadas" value={af.totals.total} />
                <Stat title="Liberadas" value={af.totals.allowed} color="#34d399" />
                <Stat title="Barradas" value={af.totals.blocked} color={af.totals.blocked > 0 ? '#fbbf24' : undefined}
                  sub={af.totals.total ? `${((af.totals.blocked / af.totals.total) * 100).toFixed(1).replace('.', ',')}% do total` : undefined} />
                <Stat title="Aprovadas pelo Asaas" value={af.totals.approved} />
                <Stat title="Recusadas pelo Asaas" value={af.totals.refused} />
              </div>

              <div className="flex gap-3 mt-3" style={{ flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div style={{ ...card, flex: '1 1 320px' }}>
                  <div style={label}>Por frente</div>
                  {af.by_front.length === 0 && <div style={{ color: '#8ba5c4', marginTop: 6 }}>Sem tentativas no período.</div>}
                  {af.by_front.map((f) => (
                    <div key={f.front} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #16304f' }}>
                      <span>{FRONT_LABEL[f.front] || f.front}</span>
                      <span>
                        <span style={{ color: '#34d399', fontWeight: 700 }}>{f.allowed}</span> liberadas ·{' '}
                        <span style={{ color: f.blocked > 0 ? '#fbbf24' : '#8ba5c4', fontWeight: 700 }}>{f.blocked}</span> barradas
                      </span>
                    </div>
                  ))}
                </div>

                <div style={{ ...card, flex: '1 1 320px' }}>
                  <div style={label}>CAPTCHA por frente</div>
                  {af.turnstile.length === 0 && <div style={{ color: '#8ba5c4', marginTop: 6 }}>Sem dados no período.</div>}
                  {af.turnstile.map((t) => (
                    <div key={t.front} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #16304f' }}>
                      <span>{FRONT_LABEL[t.front] || t.front}</span>
                      <span>
                        <span style={{ color: '#34d399', fontWeight: 700 }}>{t.ok}</span>/{t.total} ok ·{' '}
                        <span style={{ color: t.fail > 0 ? '#f87171' : '#8ba5c4', fontWeight: 700 }}>{t.fail}</span> falhou ·{' '}
                        <span style={{ color: '#8ba5c4' }}>{t.skipped}</span> sem
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ ...card, marginTop: 12 }}>
                <div style={label}>Tentativas por dia</div>
                {af.daily.length === 0 ? (
                  <div style={{ color: '#8ba5c4', marginTop: 6 }}>Sem tentativas no período.</div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 110, marginTop: 10, overflowX: 'auto' }}>
                    {af.daily.map((d) => {
                      const total = d.allowed + d.blocked;
                      const h = Math.max(4, Math.round((total / maxDaily) * 90));
                      const hb = total ? Math.round((d.blocked / total) * h) : 0;
                      return (
                        <div key={d.date} title={`${fmtDate(d.date)}: ${d.allowed} liberadas, ${d.blocked} barradas`}
                          style={{ flex: '1 0 14px', maxWidth: 34, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center' }}>
                          <div style={{ width: '100%', height: h, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', borderRadius: 3, overflow: 'hidden' }}>
                            {hb > 0 && <div style={{ height: hb, background: '#fbbf24' }} />}
                            <div style={{ height: h - hb, background: '#47B098' }} />
                          </div>
                          <div style={{ fontSize: 9, color: '#6b87a8', marginTop: 3 }}>{String(d.date).slice(8, 10)}/{String(d.date).slice(5, 7)}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div style={{ fontSize: 11, color: '#8ba5c4', marginTop: 6 }}>
                  <span style={{ color: '#47B098' }}>■</span> liberadas &nbsp; <span style={{ color: '#fbbf24' }}>■</span> barradas
                </div>
              </div>

              <div className="flex gap-3 mt-3" style={{ flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div style={{ ...card, flex: '1 1 280px' }}>
                  <div style={label}>Motivos das barradas</div>
                  {af.top_reasons.length === 0 && <div style={{ color: '#8ba5c4', marginTop: 6 }}>Nenhuma tentativa barrada no período.</div>}
                  {af.top_reasons.map((r) => (
                    <div key={r.reason} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #16304f' }}>
                      <span>{reasonText(r.reason)}</span>
                      <span style={{ fontWeight: 700, color: '#fbbf24' }}>{r.count}</span>
                    </div>
                  ))}
                </div>

                <div style={{ ...card, flex: '2 1 380px' }}>
                  <div style={label}>Últimas barradas</div>
                  {af.recent_blocked.length === 0 && <div style={{ color: '#8ba5c4', marginTop: 6 }}>Nenhuma no período.</div>}
                  {af.recent_blocked.map((a, i) => (
                    <div key={i} style={{ padding: '7px 0', borderBottom: '1px solid #16304f', fontSize: 13 }}>
                      <div>
                        <strong>{FRONT_LABEL[a.front] || a.front}</strong> · {fmtDT(a.created_at)} · {a.email || 'sem e-mail'}
                      </div>
                      <div style={{ color: '#fcd34d' }}>{a.reasons.map(reasonText).join(', ') || 'Limite de risco'}</div>
                      <div style={{ color: '#8ba5c4', fontSize: 12 }}>
                        IP {a.ip || '—'} · cartão {a.card || '—'} · score {a.score}{a.ref ? ` · ref ${a.ref}` : ''}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : <div style={{ color: '#8ba5c4' }}>Indisponível no momento.</div>}

          {/* ── 3D Secure ── */}
          <div style={sectionTitle}>🛡️ 3D Secure (confirmação do banco)</div>
          {data.three_ds ? (() => {
            const t = data.three_ds;
            const onOff = (v) => (v === null || v === undefined
              ? <span style={{ color: '#8ba5c4' }}>sem tentativas</span>
              : <span style={{ color: v ? '#34d399' : '#f87171', fontWeight: 700 }}>{v ? 'Ligado' : 'Desligado'}</span>);
            return (
              <>
                <div className="flex gap-3" style={{ flexWrap: 'wrap' }}>
                  <Stat title="Conta Asaas 1" value={onOff(t.config.accounts[1])} />
                  <Stat title="Conta Asaas 2" value={onOff(t.config.accounts[2])} />
                  <Stat title="Orçamento" value={onOff(t.config.fronts.presale)} />
                  <Stat title="App" value={onOff(t.config.fronts.app)} sub="só versão 2.4.1+" />
                  <Stat title="Site" value={onOff(t.config.fronts.site)} sub="pela última tentativa do site" />
                </div>

                {t.migrated === false ? (
                  <div style={{ color: '#fcd34d', marginTop: 12 }}>Rode a migration do 3DS para ver o funil.</div>
                ) : (
                  <>
                    <div style={{ ...card, marginTop: 12, padding: 0, overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ color: '#8ba5c4', textAlign: 'left' }}>
                            {['Frente', 'Aprovou direto', 'Pediu confirmação', 'Erro', 'Sem 3DS'].map((h) => (
                              <th key={h} style={{ padding: '10px 12px', borderBottom: '1px solid #1e3a5f', whiteSpace: 'nowrap' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {t.funnel.length === 0 && (
                            <tr><td colSpan={5} style={{ padding: 12, color: '#8ba5c4' }}>Nenhuma tentativa com cartão no período.</td></tr>
                          )}
                          {t.funnel.map((f) => (
                            <tr key={f.front} style={{ borderBottom: '1px solid #16304f' }}>
                              <td style={{ padding: '8px 12px', fontWeight: 600 }}>{FRONT_LABEL[f.front] || f.front}</td>
                              <td style={{ padding: '8px 12px', color: '#34d399' }}>{f.direto}</td>
                              <td style={{ padding: '8px 12px', color: '#fcd34d' }}>{f.desafio}</td>
                              <td style={{ padding: '8px 12px', color: f.erro > 0 ? '#f87171' : undefined }}>{f.erro}</td>
                              <td style={{ padding: '8px 12px', color: '#8ba5c4' }}>{f.desligado}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="flex gap-3" style={{ flexWrap: 'wrap', marginTop: 12 }}>
                      <div style={{ ...card, flex: '1 1 340px' }}>
                        <div style={label}>Confirmações não concluídas (+30 min)</div>
                        {t.abandoned.length === 0 && <div style={{ color: '#8ba5c4', marginTop: 6 }}>Nenhuma no período.</div>}
                        {t.abandoned.map((a, i) => (
                          <div key={i} style={{ padding: '7px 0', borderBottom: '1px solid #16304f', fontSize: 13 }}>
                            <div><strong>{FRONT_LABEL[a.front] || a.front}</strong> · {fmtDT(a.created_at)} · {a.email || 'sem e-mail'}</div>
                            <div style={{ color: '#8ba5c4', fontSize: 12 }}>cartão {a.card || '—'}{a.ref ? ` · ref ${a.ref}` : ''}</div>
                            <div style={{ color: '#fcd34d', fontSize: 12 }}>➜ {a.action}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{ ...card, flex: '1 1 340px' }}>
                        <div style={label}>Erros de 3DS / cobrança</div>
                        {t.errors.length === 0 && <div style={{ color: '#8ba5c4', marginTop: 6 }}>Nenhum no período.</div>}
                        {t.errors.map((a, i) => (
                          <div key={i} style={{ padding: '7px 0', borderBottom: '1px solid #16304f', fontSize: 13 }}>
                            <div><strong>{FRONT_LABEL[a.front] || a.front}</strong> · {fmtDT(a.created_at)} · {a.email || 'sem e-mail'}</div>
                            <div style={{ color: '#f87171' }}>{a.message || 'Sem mensagem do Asaas'}</div>
                            <div style={{ color: '#fcd34d', fontSize: 12 }}>➜ {a.action}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </>
            );
          })() : <div style={{ color: '#8ba5c4' }}>Indisponível no momento.</div>}

          {/* ── Chargebacks ── */}
          <div style={sectionTitle}>⚠️ Contestações (chargebacks)</div>
          {cb ? (
            <div className="flex gap-3" style={{ flexWrap: 'wrap' }}>
              <Stat title="Abertas agora" value={cb.open_now} color={cb.open_now > 0 ? '#f87171' : undefined} sub="todas as datas" />
              <Stat title="Novas no período" value={cb.seen_in_period} />
              <Stat title="Em aberto (do período)" value={cb.open_in_period} />
              <Stat title="Perdidas (do período)" value={cb.lost_in_period} color={cb.lost_in_period > 0 ? '#f87171' : undefined} />
            </div>
          ) : <div style={{ color: '#8ba5c4' }}>Indisponível no momento.</div>}
          <div style={{ color: '#6b87a8', fontSize: 12, marginTop: 8 }}>
            Cada parcela de uma mesma compra contestada conta como uma contestação. Para responder, abra a cobrança no painel do Asaas.
          </div>
        </>
      )}
    </div>
  );
};

export default Index;
